import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  CheckCircle2, XCircle, Clock, Volume2, VolumeX, RefreshCw, LogOut,
  PackageCheck, AlertTriangle, Loader2, Eye, EyeOff,
} from 'lucide-react';
import axios from 'axios';
import { isPaid, isFailed, isPending } from '../../components/admin/PaymentStatusPill';

const VOLUNTEER_MODE_KEY = 'nhf_kiosk_volunteer_mode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const POLL_MS = 3000;

const playSound = (type) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    // Different tones for different events
    if (type === 'paid') { o.frequency.value = 880; g.gain.value = 0.15; }
    else if (type === 'failed') { o.frequency.value = 220; g.gain.value = 0.2; }
    else { o.frequency.value = 440; g.gain.value = 0.1; }
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, type === 'failed' ? 600 : 280);
    if (type === 'failed') {
      // Two-tone klaxon
      setTimeout(() => {
        const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
        const o2 = ctx2.createOscillator(); const g2 = ctx2.createGain();
        o2.connect(g2); g2.connect(ctx2.destination);
        o2.frequency.value = 165; g2.gain.value = 0.2;
        o2.start();
        setTimeout(() => { o2.stop(); ctx2.close(); }, 600);
      }, 700);
    }
  } catch { /* ignore */ }
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  } catch { return ''; }
};

/**
 * Pop-Up Sales LIVE MODE Kiosk page — /admin/popup-live
 *
 * Full-screen black background, jumbo numbers, 3-second polling. Built for a
 * tablet at the fundraiser check-in table so volunteers know in one glance
 * whether to hand out merch.
 *
 * - GREEN "PAYMENT APPROVED — OK TO RELEASE ITEM" when the selected order is paid
 * - RED "DO NOT RELEASE ITEM — PAYMENT NOT CONFIRMED" otherwise
 * - "Mark as Released" button (disabled unless paid)
 * - Sound + toast alert when a new paid order lands (chime) or a failed one (klaxon)
 */
const PopupLiveKiosk = () => {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauth
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [toast, setToast] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [volunteerMode, setVolunteerMode] = useState(() => {
    try { return localStorage.getItem(VOLUNTEER_MODE_KEY) === '1'; } catch { return false; }
  });
  const lastIdsRef = useRef({ paid: new Set(), failed: new Set() });

  // Persist volunteer mode setting
  useEffect(() => {
    try { localStorage.setItem(VOLUNTEER_MODE_KEY, volunteerMode ? '1' : '0'); } catch { /* ignore */ }
  }, [volunteerMode]);

  // Auth check
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(r => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/popup-shop/orders/recent?hours=12&limit=50`, { withCredentials: true });
      const next = r.data;
      // Detect new orders by status to fire toast + sound
      const nextPaidIds = new Set(next.orders.filter(o => isPaid(o.status)).map(o => o.id));
      const nextFailedIds = new Set(next.orders.filter(o => isFailed(o.status)).map(o => o.id));
      // Only fire alerts after first load
      if (data) {
        for (const id of nextPaidIds) {
          if (!lastIdsRef.current.paid.has(id)) {
            const o = next.orders.find(x => x.id === id);
            if (o) {
              setToast({ kind: 'paid', message: `Paid: ${o.buyer_name} • ${fmt(o.total)}`, ts: Date.now() });
              if (soundOn) playSound('paid');
            }
          }
        }
        for (const id of nextFailedIds) {
          if (!lastIdsRef.current.failed.has(id)) {
            const o = next.orders.find(x => x.id === id);
            if (o) {
              setToast({ kind: 'failed', message: `FAILED: ${o.buyer_name} • ${fmt(o.total)}`, ts: Date.now() });
              if (soundOn) playSound('failed');
            }
          }
        }
      }
      lastIdsRef.current = { paid: nextPaidIds, failed: nextFailedIds };
      setData(next);
      // Auto-select most recent order if nothing selected
      if (!selectedId && next.orders.length > 0) setSelectedId(next.orders[0].id);
    } catch (e) {
      // silent — keep previous data on transient errors
    } finally {
      setLoading(false);
    }
  }, [data, soundOn, selectedId]);

  useEffect(() => {
    if (user === null || user === undefined) return;
    fetchOrders();
    const t = setInterval(fetchOrders, POLL_MS);
    return () => clearInterval(t);
  }, [user, fetchOrders]);

  // Auto-dismiss toast after 5s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleRelease = async (order) => {
    if (!isPaid(order.status)) return;
    setReleasing(order.id);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/popup-shop/orders/${order.id}/release`, {}, { withCredentials: true });
      await fetchOrders();
      setToast({ kind: 'released', message: `Released: ${order.buyer_name} • ${order.order_number}`, ts: Date.now() });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to mark released');
    }
    setReleasing(null);
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-10 h-10 text-white animate-spin" /></div>;
  }
  if (user === null) return <Navigate to="/admin" replace />;

  const selectedOrder = data?.orders.find(o => o.id === selectedId) || data?.orders[0] || null;
  const paid = selectedOrder && isPaid(selectedOrder.status);
  const failed = selectedOrder && isFailed(selectedOrder.status);
  const pending = selectedOrder && isPending(selectedOrder.status);
  const released = selectedOrder && !!selectedOrder.released_at;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="popup-live-kiosk">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-[#BA1826] animate-pulse" />
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-white/80">Pop-Up Live Mode</p>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-white/40" />}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setVolunteerMode(v => !v)} title={volunteerMode ? 'Show admin details' : 'Hide sensitive admin info'}
            className={`text-xs px-3 py-1.5 rounded border flex items-center gap-1.5 ${volunteerMode ? 'bg-blue-500/25 border-blue-400 text-blue-100' : 'bg-white/5 border-white/20 text-white/60'}`}
            data-testid="kiosk-volunteer-mode-toggle">
            {volunteerMode ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {volunteerMode ? 'Volunteer Mode' : 'Admin View'}
          </button>
          <button onClick={() => setSoundOn(s => !s)} title={soundOn ? 'Mute' : 'Unmute'}
            className={`text-xs px-3 py-1.5 rounded border flex items-center gap-1.5 ${soundOn ? 'bg-amber-500/20 border-amber-400 text-amber-200' : 'bg-white/5 border-white/20 text-white/60'}`}
            data-testid="kiosk-sound-toggle">
            {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            {soundOn ? 'Sound ON' : 'Muted'}
          </button>
          <button onClick={fetchOrders} className="text-xs px-3 py-1.5 rounded border border-white/20 text-white/80 hover:bg-white/5 flex items-center gap-1.5" data-testid="kiosk-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <a href="/admin" className="text-xs px-3 py-1.5 rounded border border-white/20 text-white/80 hover:bg-white/5 flex items-center gap-1.5" data-testid="kiosk-exit-btn">
            <LogOut className="w-3.5 h-3.5" /> Exit
          </a>
        </div>
      </header>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-16 right-6 z-50 max-w-md px-5 py-3 rounded-lg shadow-2xl border-2 animate-in fade-in slide-in-from-top-2 ${
          toast.kind === 'paid' ? 'bg-green-600 border-green-400 text-white' :
          toast.kind === 'failed' ? 'bg-red-700 border-red-500 text-white' :
          'bg-[#BA1826] border-red-400 text-white'
        }`} data-testid="kiosk-toast">
          <div className="flex items-center gap-2 font-bold">
            {toast.kind === 'paid' && <CheckCircle2 className="w-5 h-5" />}
            {toast.kind === 'failed' && <AlertTriangle className="w-5 h-5" />}
            {toast.kind === 'released' && <PackageCheck className="w-5 h-5" />}
            {volunteerMode
              ? (toast.kind === 'paid' ? 'New paid order ready' :
                 toast.kind === 'failed' ? 'Payment FAILED — do not release' :
                 'Order released')
              : toast.message}
          </div>
        </div>
      )}

      {/* Stats row — jumbo (hidden in volunteer mode) */}
      {!volunteerMode && (
      <section className="grid grid-cols-4 gap-4 px-6 py-5 border-b border-white/10" data-testid="kiosk-stats">
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] text-green-300 font-bold mb-1">Confirmed Revenue</p>
          <p className="font-heading text-5xl lg:text-6xl font-bold font-mono text-green-400" data-testid="kiosk-confirmed-revenue">{fmt(data?.paid_revenue || 0)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] text-green-300 font-bold mb-1">Paid Orders</p>
          <p className="font-heading text-5xl lg:text-6xl font-bold text-green-400" data-testid="kiosk-paid-count">{data?.paid_count || 0}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold mb-1">Pending</p>
          <p className="font-heading text-5xl lg:text-6xl font-bold text-amber-400" data-testid="kiosk-pending-count">{data?.pending_count || 0}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] text-red-300 font-bold mb-1">Failed</p>
          <p className="font-heading text-5xl lg:text-6xl font-bold text-red-400" data-testid="kiosk-failed-count">{data?.failed_count || 0}</p>
        </div>
      </section>
      )}

      {/* Volunteer-mode summary strip — counts only, no revenue */}
      {volunteerMode && (
        <section className="grid grid-cols-3 gap-4 px-6 py-4 border-b border-white/10" data-testid="kiosk-stats-volunteer">
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-green-300 font-bold mb-1">Paid Orders</p>
            <p className="font-heading text-4xl font-bold text-green-400" data-testid="kiosk-paid-count">{data?.paid_count || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold mb-1">Pending</p>
            <p className="font-heading text-4xl font-bold text-amber-400" data-testid="kiosk-pending-count">{data?.pending_count || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-red-300 font-bold mb-1">Failed</p>
            <p className="font-heading text-4xl font-bold text-red-400">{data?.failed_count || 0}</p>
          </div>
        </section>
      )}

      {/* Main split: order list + selected order detail */}
      <main className="flex-1 grid lg:grid-cols-2 gap-4 px-6 py-5 min-h-0">
        {/* Order feed */}
        <div className="flex flex-col min-h-0">
          <h3 className="text-xs uppercase tracking-[0.25em] text-white/60 font-bold mb-2">Recent Orders</h3>
          <div className="flex-1 overflow-y-auto space-y-2 pr-2" data-testid="kiosk-order-list">
            {(data?.orders || []).length === 0 ? (
              <p className="text-sm text-white/40 text-center py-8">No orders yet in the last 12 hours.</p>
            ) : (data?.orders || []).map(o => {
              const oPaid = isPaid(o.status);
              const oFailed = isFailed(o.status);
              const oReleased = !!o.released_at;
              return (
                <button
                  key={o.id}
                  onClick={() => setSelectedId(o.id)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    selectedId === o.id ? 'border-white bg-white/10' :
                    oFailed ? 'border-red-700 bg-red-950/40 hover:bg-red-950/60' :
                    oPaid ? 'border-green-700 bg-green-950/40 hover:bg-green-950/60' :
                    'border-amber-700 bg-amber-950/40 hover:bg-amber-950/60'
                  }`}
                  data-testid={`kiosk-order-${o.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="font-heading text-sm font-bold truncate">{o.buyer_name || '—'}</p>
                    {!volunteerMode && <p className="font-mono text-base font-bold flex-shrink-0">{fmt(o.total)}</p>}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-white/60 font-mono truncate">{o.order_number}</span>
                    <span className={`font-bold uppercase tracking-wide ${oPaid ? 'text-green-400' : oFailed ? 'text-red-400' : 'text-amber-400'}`}>
                      {oPaid ? (oReleased ? '✓ Released' : 'Paid') : oFailed ? 'Failed' : o.status}
                    </span>
                  </div>
                  <p className="text-[10px] text-white/40 mt-0.5">{fmtTime(o.created_at)}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected order detail — the big GO/NO-GO panel */}
        <div className="flex flex-col">
          <h3 className="text-xs uppercase tracking-[0.25em] text-white/60 font-bold mb-2">Selected Order</h3>
          {!selectedOrder ? (
            <div className="flex-1 flex items-center justify-center bg-white/5 rounded-lg border border-white/10">
              <p className="text-sm text-white/40">Tap an order on the left to view details.</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col">
              {/* GO / NO-GO HERO */}
              {paid && !released && (
                <div className="bg-green-600 text-white rounded-xl p-6 mb-3 text-center" data-testid="kiosk-payment-approved">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-2" strokeWidth={3} />
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-1">PAYMENT APPROVED</h2>
                  <p className="text-base font-bold uppercase tracking-wide opacity-90">OK to Release Item</p>
                </div>
              )}
              {paid && released && (
                <div className="bg-blue-700 text-white rounded-xl p-6 mb-3 text-center" data-testid="kiosk-released">
                  <PackageCheck className="w-16 h-16 mx-auto mb-2" strokeWidth={3} />
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-1">ITEM RELEASED</h2>
                  <p className="text-sm opacity-90">Released {fmtTime(selectedOrder.released_at)} by {selectedOrder.released_by}</p>
                </div>
              )}
              {failed && (
                <div className="bg-red-700 text-white rounded-xl p-6 mb-3 text-center animate-pulse" data-testid="kiosk-do-not-release">
                  <XCircle className="w-16 h-16 mx-auto mb-2" strokeWidth={3} />
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-1">DO NOT RELEASE ITEM</h2>
                  <p className="text-base font-bold uppercase tracking-wide opacity-90">Payment Failed</p>
                </div>
              )}
              {pending && (
                <div className="bg-amber-600 text-white rounded-xl p-6 mb-3 text-center" data-testid="kiosk-do-not-release-pending">
                  <Clock className="w-16 h-16 mx-auto mb-2" strokeWidth={3} />
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-1">DO NOT RELEASE ITEM</h2>
                  <p className="text-base font-bold uppercase tracking-wide opacity-90">Payment Not Confirmed</p>
                </div>
              )}

              {/* Order detail */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-mono text-base font-bold">{selectedOrder.order_number}</p>
                  {!volunteerMode && <p className="font-mono text-xl font-bold">{fmt(selectedOrder.total)}</p>}
                </div>
                {volunteerMode ? (
                  <p className="text-sm">{selectedOrder.buyer_name}</p>
                ) : (
                  <p className="text-sm">{selectedOrder.buyer_name} <span className="text-white/40">·</span> <span className="text-white/60">{selectedOrder.buyer_email}</span></p>
                )}
                <div className="text-sm text-white/80 pt-2 border-t border-white/10 space-y-1">
                  {(selectedOrder.line_items || []).map((li, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{li.item_name} × {li.quantity}</span>
                      {!volunteerMode && <span className="font-mono">{fmt(li.line_total)}</span>}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-white/50 pt-2 border-t border-white/10">
                  Order placed {fmtTime(selectedOrder.created_at)}
                </p>
                {selectedOrder.failed_reason && !volunteerMode && (
                  <p className="text-xs text-red-300 mt-2 pt-2 border-t border-red-900/40"><strong>Reason:</strong> {selectedOrder.failed_reason}</p>
                )}
              </div>

              {/* Release button */}
              <button
                onClick={() => handleRelease(selectedOrder)}
                disabled={!paid || released || releasing === selectedOrder.id}
                className={`mt-3 w-full py-5 rounded-xl font-heading text-2xl font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-3 ${
                  paid && !released
                    ? 'bg-white text-black hover:bg-white/90 active:scale-[0.98]'
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                }`}
                data-testid="kiosk-release-btn"
              >
                {releasing === selectedOrder.id ? <Loader2 className="w-6 h-6 animate-spin" /> :
                 released ? <><PackageCheck className="w-6 h-6" /> Already Released</> :
                 paid ? <><PackageCheck className="w-6 h-6" /> Mark Item Released</> :
                 <><XCircle className="w-6 h-6" /> Locked — payment required</>}
              </button>
            </div>
          )}
        </div>
      </main>

      <footer className="px-6 py-2 border-t border-white/10 text-[10px] text-white/40 flex items-center justify-between">
        <span>Live polling every 3 seconds · {data?.window_hours || 12}h window</span>
        {!volunteerMode && <span>Logged in as {user?.email}</span>}
        {volunteerMode && <span className="text-blue-300">Volunteer Mode · sensitive data hidden</span>}
      </footer>
    </div>
  );
};

export default PopupLiveKiosk;
